import React, { useState } from 'react';
import { Card, Button, Table, Tag, Space, Typography, Alert, Spin } from 'antd';
import { ExperimentOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { apiService } from '../../services/api';

const { Title, Text } = Typography;

interface ModelResult {
  provider: string;
  model: string;
  status: string;
  response?: string;
  error?: string;
  errorType?: string;
  duration?: number;
}

export const ModelTesting: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<{ openai: ModelResult[]; anthropic: ModelResult[] } | null>(null);
  const [workingModels, setWorkingModels] = useState<{ openai: string[]; anthropic: string[] } | null>(null);

  const runModelTests = async () => {
    setIsLoading(true);
    try {
      // Use the private request method by accessing the API service baseUrl and making a direct fetch
      const token = await apiService.getAuthToken();
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      const baseUrl = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${baseUrl}/api/models/test-all`, {
        method: 'POST',
        headers
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setResults(data.results);
        setWorkingModels(data.workingModels);
      } else {
        throw new Error(data.error || 'Test failed');
      }
    } catch (error) {
      console.error('Model testing error:', error);
      // Show error in results
      setResults({
        openai: [],
        anthropic: []
      });
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      render: (model: string) => <Text strong>{model}</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const config = {
          working: { color: 'green', text: '✅ Working' },
          error: { color: 'red', text: '❌ Error' },
          empty_response: { color: 'orange', text: '⚠️ Empty Response' },
        }[status] || { color: 'gray', text: status };
        
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: 'Details',
      dataIndex: 'response',
      key: 'details',
      render: (_: any, record: ModelResult) => {
        if (record.status === 'working') {
          return (
            <Space direction="vertical" size="small">
              <Text>Response: "{record.response}"</Text>
              <Text type="secondary">Duration: {record.duration}ms</Text>
            </Space>
          );
        } else {
          return (
            <Space direction="vertical" size="small">
              <Text type="danger">{record.error}</Text>
              {record.errorType && <Text type="secondary">Type: {record.errorType}</Text>}
              {record.duration && <Text type="secondary">Duration: {record.duration}ms</Text>}
            </Space>
          );
        }
      },
    },
  ];

  return (
    <Card
      title={
        <Space>
          <ExperimentOutlined />
          Model Testing
        </Space>
      }
      extra={
        <Button
          type="primary"
          icon={<PlayCircleOutlined />}
          onClick={runModelTests}
          loading={isLoading}
        >
          Test All Models
        </Button>
      }
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Alert
          message="Model Testing"
          description="Test all available OpenAI and Anthropic models to see which ones work with your current API keys. This helps ensure accurate model configuration."
          type="info"
          showIcon
        />

        {isLoading && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <Spin size="large" />
            <div style={{ marginTop: '1rem' }}>
              <Text>Testing all models... This may take 1-2 minutes.</Text>
            </div>
          </div>
        )}

        {results && !isLoading && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Summary */}
            <Alert
              message="Test Results Summary"
              description={
                <Space direction="vertical">
                  <Text>OpenAI: {workingModels?.openai.length || 0} / {results.openai.length} models working</Text>
                  <Text>Anthropic: {workingModels?.anthropic.length || 0} / {results.anthropic.length} models working</Text>
                </Space>
              }
              type="success"
              showIcon
            />

            {/* OpenAI Results */}
            <div>
              <Title level={4}>OpenAI Models</Title>
              <Table
                dataSource={results.openai}
                columns={columns}
                pagination={false}
                size="small"
                rowKey="model"
              />
            </div>

            {/* Anthropic Results */}
            <div>
              <Title level={4}>Anthropic Models</Title>
              <Table
                dataSource={results.anthropic}
                columns={columns}
                pagination={false}
                size="small"
                rowKey="model"
              />
            </div>

            {/* Working Models Summary */}
            {workingModels && (
              <Card title="Working Models (for configuration)" size="small">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <Text strong>OpenAI:</Text>
                    <div style={{ marginTop: 8 }}>
                      {workingModels.openai.map(model => (
                        <Tag key={model} color="green">{model}</Tag>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Text strong>Anthropic:</Text>
                    <div style={{ marginTop: 8 }}>
                      {workingModels.anthropic.map(model => (
                        <Tag key={model} color="blue">{model}</Tag>
                      ))}
                    </div>
                  </div>
                </Space>
              </Card>
            )}
          </Space>
        )}
      </Space>
    </Card>
  );
};